import React, { useState, useEffect } from 'react';
import { externalBookingAPI } from '@/api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Clock, MapPin, Phone, Mail, User, Upload, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatTime12Hour, formatTimeRange } from '@/utils/formatters';

const ExternalMockBooking = () => {
  const [mocks, setMocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ mock_fee: 0, bkash_number: '01995432873', max_file_size_mb: 1, show_seats: true });

  // Booking flow states
  const [selectedMock, setSelectedMock] = useState(null);
  const [speakingSlots, setSpeakingSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [speakingMode, setSpeakingMode] = useState('OFFLINE');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [showSpeakingModal, setShowSpeakingModal] = useState(false);
  const [showModeModal, setShowModeModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Candidate details
  const [candidateName, setCandidateName] = useState('');
  const [candidateMobile, setCandidateMobile] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [previousMock, setPreviousMock] = useState('no');
  const [mockCount, setMockCount] = useState('');
  const [paymentFile, setPaymentFile] = useState(null);

  useEffect(() => {
    document.title = 'Mock Test Booking | Radiance Education';
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [examsRes, settingsRes] = await Promise.all([
        externalBookingAPI.getMockExams(),
        externalBookingAPI.getPublicSettings()
      ]);
      setMocks(examsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMock = async (mock) => {
    if (mock.booking_closed || mock.lrw_remaining_seats <= 0) return;
    try {
      const res = await externalBookingAPI.getSpeakingSlots(mock.id);
      setSpeakingSlots(res.data);
      setSelectedMock(mock);
      setShowSpeakingModal(true);
    } catch (error) {
      toast.error('Failed to load speaking slots');
    }
  };

  const handleSlotSelect = (slot) => {
    if (slot.is_full) return;
    setSelectedSlot(slot);
    setShowSpeakingModal(false);
    setShowModeModal(true);
  };

  const handleModeConfirm = () => {
    if (speakingMode === 'ONLINE') {
      const digits = whatsappNumber.replace(/\D/g, '');
      if (!digits || digits.length !== 11) {
        toast.error('WhatsApp number must be 11 digits');
        return;
      }
    }
    setShowModeModal(false);
    setShowDetailsModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      toast.error('Only JPG/PNG files are allowed');
      return;
    }
    const maxMb = settings.max_file_size_mb || 1;
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`File size must be less than ${maxMb} MB`);
      return;
    }
    setPaymentFile(file);
  };

  const handleSubmit = async () => {
    if (!candidateName.trim() || !candidateMobile.trim() || !candidateEmail.trim()) {
      toast.error('Please fill all required fields');
      return;
    }
    if (previousMock === 'yes') {
      const n = parseInt(mockCount, 10);
      if (!mockCount || isNaN(n) || n < 1) {
        toast.error('Please enter your previous mock number (e.g. 2)');
        return;
      }
    }
    if (!paymentFile) {
      toast.error('Please upload payment screenshot');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('mock_id', selectedMock.id);
      formData.append('speak_slot_id', selectedSlot.id);
      formData.append('speak_mode', speakingMode);
      if (speakingMode === 'ONLINE') formData.append('whatsapp_number', whatsappNumber);
      formData.append('candidate_name', candidateName.trim());
      formData.append('candidate_mobile', candidateMobile.trim());
      formData.append('candidate_email', candidateEmail.trim());
      formData.append('previous_mock', previousMock);
      if (previousMock === 'yes' && mockCount) formData.append('mock_count', mockCount);
      formData.append('payment_screenshot', paymentFile);

      await externalBookingAPI.submitBooking(formData);
      setShowDetailsModal(false);
      setShowSuccess(true);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to submit booking';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setSelectedMock(null);
    setSelectedSlot(null);
    setSpeakingMode('OFFLINE');
    setWhatsappNumber('');
    setCandidateName('');
    setCandidateMobile('');
    setCandidateEmail('');
    setPreviousMock('no');
    setMockCount('');
    setPaymentFile(null);
    setShowSuccess(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-900 text-white py-6 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <img src="https://radianceedu.com/wp-content/uploads/2025/11/Logo-Radiance-Education.png" alt="Radiance Education" className="h-12 mx-auto mb-3" />
          <h1 className="text-2xl sm:text-3xl font-bold">IELTS Mock Test Booking</h1>
          <p className="text-blue-200 mt-1 text-sm">For External Candidates</p>
          {settings.mock_fee > 0 && (
            <Badge className="mt-2 bg-white/20 text-white text-sm">Mock Test Fee: BDT {settings.mock_fee}</Badge>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        {mocks.length === 0 ? (
          <Card className="mt-6">
            <CardContent className="py-12 text-center">
              <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <h3 className="text-lg font-medium text-gray-600">No Mock Tests Available</h3>
              <p className="text-gray-400 text-sm mt-1">Please check back later for upcoming mock tests</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4 mt-4">
            <h2 className="text-lg font-semibold text-gray-700">Available Mock Tests</h2>
            {mocks.map((mock) => {
              const isClosed = mock.booking_closed || mock.lrw_remaining_seats <= 0;
              return (
                <Card key={mock.id} data-testid={`mock-card-${mock.id}`} className={`border ${isClosed ? 'opacity-60 border-gray-200' : 'border-blue-200 hover:shadow-md'} transition-shadow`}>
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-base">{mock.mock_uid || mock.name}</h3>
                          <Badge variant={isClosed ? 'secondary' : 'default'} className={isClosed ? '' : 'bg-green-600'}>
                            {isClosed ? 'CLOSED' : 'OPEN'}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                          <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{formatDate(mock.lrw_date)}</span>
                          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{formatTimeRange(mock.lrw_start, mock.lrw_end)}</span>
                          {mock.campus && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{mock.campus}</span>}
                        </div>
                        <div className="text-xs">
                          {settings.show_seats !== false && (
                            <span className={mock.lrw_remaining_seats > 0 ? 'text-green-600 font-semibold' : 'text-red-500 font-semibold'}>
                              {mock.lrw_remaining_seats} Seats Left
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        data-testid={`book-mock-${mock.id}`}
                        onClick={() => handleSelectMock(mock)}
                        disabled={isClosed}
                        className="shrink-0"
                      >
                        {isClosed ? 'Unavailable' : 'Book Now'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Contact Info */}
        <div className="mt-8 text-center">
          <a
            href="tel:01759787364"
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all"
          >
            <Phone className="h-5 w-5" />
            Need help? Call us: 01759787364
          </a>
        </div>
      </div>

      {/* Speaking Slot Selection Modal */}
      <Dialog open={showSpeakingModal} onOpenChange={setShowSpeakingModal}>
        <DialogContent className="w-[calc(100%-8px)] max-w-lg max-h-[90vh] modal-scroll p-0 border-0 bg-transparent shadow-none sm:rounded-2xl">
          <div className="relative bg-white/85 backdrop-blur-2xl rounded-2xl shadow-2xl ring-1 ring-blue-100/60 overflow-hidden">
            {/* Decorative gradient blobs */}
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-blue-400/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-indigo-400/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative px-6 pt-6 pb-3 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700">
              <DialogHeader>
                <DialogTitle className="text-white text-lg sm:text-xl font-semibold tracking-tight">Select Speaking Slot</DialogTitle>
                <p className="text-blue-100/80 text-xs mt-1">Choose a convenient time for your speaking session</p>
              </DialogHeader>
            </div>

            <div className="relative p-5 sm:p-6 space-y-2.5">
              {speakingSlots.length === 0 ? (
                <p className="text-center py-6 text-gray-500">No speaking slots available</p>
              ) : (
                speakingSlots.map((slot) => (
                  <div
                    key={slot.id}
                    data-testid={`speaking-slot-${slot.id}`}
                    onClick={() => !slot.is_full && handleSlotSelect(slot)}
                    className={`group p-4 rounded-xl transition-all duration-200 border-2 ${slot.is_full
                      ? 'bg-gradient-to-r from-red-100 to-orange-100 border-red-300 opacity-80 cursor-not-allowed'
                      : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-300 hover:shadow-lg hover:-translate-y-0.5 cursor-pointer'}`}
                  >
                    <div className="flex justify-between items-center gap-3">
                      <div>
                        <p className="font-semibold text-sm text-blue-900">{formatDate(slot.speak_date)}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{formatTimeRange(slot.start_time, slot.end_time)} {slot.room ? `| ${slot.room}` : ''}</p>
                      </div>
                      <Badge className={slot.is_full
                        ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white border-0 font-semibold'
                        : 'bg-blue-600 hover:bg-blue-700 text-white border-0 font-semibold'}>
                        {slot.is_full ? 'This Slot is Taken' : 'Book'}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mode Selection Modal */}
      <Dialog open={showModeModal} onOpenChange={setShowModeModal}>
        <DialogContent className="w-[calc(100%-8px)] max-w-md p-0 border-0 bg-transparent shadow-none sm:rounded-2xl">
          <div className="relative bg-white/85 backdrop-blur-2xl rounded-2xl shadow-2xl ring-1 ring-blue-100/60 overflow-hidden">
            <div className="absolute -top-16 -right-16 w-48 h-48 bg-blue-400/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-indigo-400/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative px-6 pt-6 pb-4 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700">
              <DialogHeader>
                <DialogTitle className="text-white text-lg sm:text-xl font-semibold tracking-tight">Speaking Mode</DialogTitle>
                <p className="text-blue-100/80 text-xs mt-1">How would you like to take the speaking test?</p>
              </DialogHeader>
            </div>

            <div className="relative p-5 sm:p-6 space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setSpeakingMode('OFFLINE')}
                  className={`relative p-4 rounded-xl border transition-all duration-200 text-sm font-medium ${speakingMode === 'OFFLINE'
                    ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white border-transparent shadow-lg shadow-blue-500/30'
                    : 'bg-white/70 border-blue-100 text-blue-800 hover:border-blue-300 hover:bg-blue-50/80'}`}
                >
                  Offline<br /><span className={`text-xs ${speakingMode === 'OFFLINE' ? 'text-blue-100' : 'text-gray-500'}`}>(In-Person)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSpeakingMode('ONLINE')}
                  className={`relative p-4 rounded-xl border transition-all duration-200 text-sm font-medium ${speakingMode === 'ONLINE'
                    ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white border-transparent shadow-lg shadow-blue-500/30'
                    : 'bg-white/70 border-blue-100 text-blue-800 hover:border-blue-300 hover:bg-blue-50/80'}`}
                >
                  Online<br /><span className={`text-xs ${speakingMode === 'ONLINE' ? 'text-blue-100' : 'text-gray-500'}`}>(WhatsApp)</span>
                </button>
              </div>
              {speakingMode === 'ONLINE' && (
                <div className="space-y-2">
                  <Label className="text-blue-900 text-sm font-medium">WhatsApp Number</Label>
                  <Input
                    placeholder="01XXXXXXXXX"
                    value={whatsappNumber}
                    onChange={(e) => setWhatsappNumber(e.target.value)}
                    maxLength={11}
                    className="bg-white/80 border-blue-200 focus-visible:ring-blue-500 h-11 rounded-lg"
                  />
                  {/* Important Notice for Online Speaking */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                    <p className="text-sm text-blue-800 font-medium mb-1">📱 Important Notice:</p>
                    <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                      <li>আপনার নেটওয়ার্ক যেন স্থিতিশীল থাকে তা নিশ্চিত করুন</li>
                      <li>আমরা সময়মতো WhatsApp এ কল দিব, ইনশাআল্লাহ</li>
                      <li>সময়মতো কল পিক আপ না করলে অনুপস্থিত ধরে নেয়া হবে</li>
                    </ul>
                  </div>
                </div>
              )}
              <Button
                onClick={handleModeConfirm}
                className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-medium rounded-lg shadow-lg shadow-blue-500/30 transition-all"
              >
                Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Candidate Details + Payment Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="w-[calc(100%-8px)] max-w-lg max-h-[92vh] modal-scroll p-0 border-0 bg-transparent shadow-none sm:rounded-2xl">
          <div className="relative bg-white/85 backdrop-blur-2xl rounded-2xl shadow-2xl ring-1 ring-blue-100/60 overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-indigo-400/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative px-6 pt-6 pb-4 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700">
              <DialogHeader>
                <DialogTitle className="text-white text-lg sm:text-xl font-semibold tracking-tight">Complete Your Booking</DialogTitle>
                <p className="text-blue-100/80 text-xs mt-1">Fill in your details to confirm the mock test</p>
              </DialogHeader>
            </div>

            <div className="relative p-5 sm:p-6 space-y-5">
              {/* Booking Summary */}
              <div className="relative bg-gradient-to-br from-blue-50/90 via-indigo-50/80 to-blue-50/70 backdrop-blur-sm border border-blue-200/60 rounded-xl p-4 shadow-sm">
                <p className="font-semibold text-blue-800 text-sm mb-2 flex items-center gap-1.5">
                  <span className="w-1 h-4 bg-gradient-to-b from-blue-600 to-indigo-700 rounded-full" />
                  Booking Summary
                </p>
                <div className="space-y-1 text-xs sm:text-sm text-blue-900/90 pl-3">
                  <p><span className="text-blue-600 font-medium">LRW:</span> {selectedMock && formatDate(selectedMock.lrw_date)} | {selectedMock && formatTimeRange(selectedMock.lrw_start, selectedMock.lrw_end)}</p>
                  <p><span className="text-blue-600 font-medium">Speaking:</span> {selectedSlot && formatDate(selectedSlot.speak_date)} | {selectedSlot && formatTimeRange(selectedSlot.start_time, selectedSlot.end_time)}</p>
                  <p><span className="text-blue-600 font-medium">Mode:</span> {speakingMode}</p>
                </div>
              </div>

              {/* Personal Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5 text-blue-900 text-sm font-medium"><User className="h-3.5 w-3.5 text-blue-600" />Full Name <span className="text-red-500">*</span></Label>
                  <Input data-testid="candidate-name" value={candidateName} onChange={(e) => setCandidateName(e.target.value)} placeholder="Your full name" className="bg-white/80 border-blue-200 focus-visible:ring-blue-500 h-11 rounded-lg" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5 text-blue-900 text-sm font-medium"><Phone className="h-3.5 w-3.5 text-blue-600" />Mobile Number <span className="text-red-500">*</span></Label>
                  <Input data-testid="candidate-mobile" value={candidateMobile} onChange={(e) => setCandidateMobile(e.target.value)} placeholder="01XXXXXXXXX" maxLength={11} className="bg-white/80 border-blue-200 focus-visible:ring-blue-500 h-11 rounded-lg" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5 text-blue-900 text-sm font-medium"><Mail className="h-3.5 w-3.5 text-blue-600" />Email Address <span className="text-red-500">*</span></Label>
                  <Input data-testid="candidate-email" type="email" value={candidateEmail} onChange={(e) => setCandidateEmail(e.target.value)} placeholder="your@email.com" className="bg-white/80 border-blue-200 focus-visible:ring-blue-500 h-11 rounded-lg" />
                </div>
                <div className="space-y-2">
                  <Label className="text-blue-900 text-sm font-medium">Have you taken mock test at Radiance before?</Label>
                  <Select value={previousMock} onValueChange={setPreviousMock}>
                    <SelectTrigger className="bg-white/80 border-blue-200 h-11 rounded-lg focus:ring-blue-500"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {previousMock === 'yes' && (
                  <div className="space-y-2">
                    <Label className="text-blue-900 text-sm font-medium">Radiance এ এটা কততম Mock? <span className="text-red-500">*</span></Label>
                    <Input
                      data-testid="previous-mock-count"
                      type="number"
                      min="1"
                      max="50"
                      value={mockCount}
                      onChange={(e) => setMockCount(e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="e.g. 3"
                      required
                      className="bg-white/80 border-blue-200 focus-visible:ring-blue-500 h-11 rounded-lg"
                    />
                  </div>
                )}
              </div>

              {/* Payment Section */}
              <div className="pt-4 border-t border-blue-100/80">
                <h3 className="font-semibold text-sm mb-3 text-blue-800 flex items-center gap-1.5">
                  <span className="w-1 h-4 bg-gradient-to-b from-blue-600 to-indigo-700 rounded-full" />
                  Payment
                </h3>
                <div className="bg-gradient-to-br from-blue-50/90 to-indigo-50/80 backdrop-blur-sm border border-blue-200/60 rounded-xl p-4 mb-4 shadow-sm space-y-1.5">
                  <p className="text-sm text-blue-900 leading-relaxed">
                    পেমেন্ট এর স্ক্রিনশট নিচে আপলোড করুন
                  </p>
                  <p className="text-sm text-blue-900">
                    আমাদের বিকাশ মার্চেন্ট নাম্বার: <strong className="text-blue-900 tracking-wide font-bold">{settings.bkash_number}</strong>
                  </p>
                  <p className="text-xs text-blue-600/90">
                    Bkash এর Payment option এ গিয়ে payment করে screenshot upload করুন
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5 text-blue-900 text-sm font-medium"><Upload className="h-3.5 w-3.5 text-blue-600" />Payment Screenshot <span className="text-red-500">*</span> <span className="text-xs text-gray-500 font-normal">(JPG/PNG, max {settings.max_file_size_mb || 1}MB)</span></Label>
                  <Input
                    data-testid="payment-screenshot"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    onChange={handleFileChange}
                    className="bg-white/80 border-blue-200 rounded-lg h-11 file:bg-blue-50 file:text-blue-700 file:border-0 file:rounded-md file:px-3 file:py-1.5 file:mr-3 file:text-xs file:font-medium hover:file:bg-blue-100 cursor-pointer"
                  />
                  {paymentFile && <p className="text-xs text-blue-600 mt-1 flex items-center gap-1"><CheckCircle className="h-3 w-3" />{paymentFile.name}</p>}
                </div>
              </div>

              <Button
                data-testid="submit-booking-btn"
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-semibold rounded-lg shadow-lg shadow-blue-500/30 transition-all disabled:opacity-60"
              >
                {submitting ? 'Submitting...' : 'Confirm Booking'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={showSuccess} onOpenChange={() => { resetAll(); fetchData(); }}>
        <DialogContent className="w-[calc(100%-8px)] max-w-md p-0 border-0 bg-transparent shadow-none sm:rounded-2xl">
          <div className="relative bg-white/90 backdrop-blur-2xl rounded-2xl shadow-2xl ring-1 ring-blue-100/60 overflow-hidden">
            <div className="absolute -top-20 -right-20 w-56 h-56 bg-emerald-400/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-56 h-56 bg-blue-400/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative px-6 pt-8 pb-6 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/40 mb-4 ring-4 ring-blue-100/60">
                <CheckCircle className="h-10 w-10 text-white" strokeWidth={2.5} />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent mb-2">Booking Submitted!</h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-5 px-2">
                Your mock test booking has been submitted for approval. You will receive an email once it is reviewed by the institute.
              </p>
              <div className="bg-gradient-to-br from-blue-50/90 to-indigo-50/80 backdrop-blur-sm rounded-xl p-4 border border-blue-200/60 shadow-sm">
                <p className="text-blue-700 text-sm font-medium">Need help? Contact us:</p>
                <a href="tel:01759787364" className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-semibold px-6 py-2.5 rounded-full mt-3 transition-all shadow-lg shadow-blue-500/30">
                  <Phone className="h-4 w-4" />01759787364
                </a>
              </div>
              <Button
                onClick={() => { resetAll(); fetchData(); }}
                className="mt-5 w-full h-11 bg-white/80 hover:bg-white border border-blue-200 text-blue-700 font-medium rounded-lg transition-all"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExternalMockBooking;
